import React, { useEffect, useMemo, useState } from "react"
import { graphql } from "gatsby"
import useRecipeQuery from "../../../hooks/use-recipe-query"
import RecipeCard from "../../cards/recipe"
import { Asset, Link } from "../../../../plugins/gatsby-plugin-atollon"
import Select from "react-select"
import { BoneLogo, EdlynLogo, GreenWheatLogo, Search, WoodsLogo } from "../../icons"
import parse from "html-react-parser"
import sal from "sal.js"

const items = [
  {
    brand: "edlyn",
    content: "Seriously good cooking companions.",
    button: "Edlyn Recipes",
    src: "https://www.datocms-assets.com/124627/1714963228-edlyn-rollover.jpg?auto=format&w=723",
  },
  {
    brand: "bone",
    content: "Precisely crafted, chef inspired stocks and jus that elevate the flavour of every dish.",
    button: "Bone Roasters Recipes",
    src: "https://www.datocms-assets.com/124627/1714963249-bone-rollover.jpg?auto=format&w=723",
  },
  {
    brand: "woods",
    content: "Renowned for making Australia’s finest condiments.",
    button: "Woods Recipes",
    src: "https://www.datocms-assets.com/124627/1714963262-woods-rollover.jpg?auto=format&w=723",
  },
  {
    brand: "greenwheat",
    content: "Award winning natural grains, grown and produced in South Australia.",
    button: "Greenwheat Recipes",
    src: "https://www.datocms-assets.com/124627/1714963277-greenwheat-rollover.jpg?auto=format&w=723",
  },
]

const RecipeArchive = ({ title, theme }) => {
  const recipes = useRecipeQuery()
  const [state, setState] = useState({})
  const [currentTag, setCurrentTag] = useState()
  const [searchText, setSearchText] = useState('')
  const [brand, setBrand] = useState('')
  
  // Filter recipes based on theme first
  const initialFilteredRecipes = useMemo(() => {
    return theme ? recipes.filter(recipe => recipe.brand === theme) : recipes;
  }, [theme, recipes]);

  // Generate tags based on initial filtered list
  const tags = [...new Set([...initialFilteredRecipes.flatMap(recipe => recipe.tags.map(tag => tag.title))].filter(Boolean))];
  const filteredTags = tags.filter(tag => !["Greenwheat Freekeh", "Edlyn", "Bone Roasters", "Woods"].includes(tag));
  
  // Store initial filtered tags
  const [initialFilteredTags] = useState(filteredTags);

  const options = [{
      value: '',
      label: 'Filter'
    },
    ...initialFilteredTags.map(el => ({
      value: el?.toLowerCase().trim().split(' ').join('-'),
      label: el
    })) || []
  ]

  useEffect(() => {
    sal()
  }, [currentTag, searchText, brand])

  const handleSubmit = (e) => {
    e.preventDefault()
    const value = e.target.search.value
    setState({ ...state, search: value })
  }

  const filteredRecipes = useMemo(() => {
    let filteredToRecipes = initialFilteredRecipes;

    if (currentTag) {
      filteredToRecipes = filteredToRecipes.filter(el =>
        el.tags.some(tag =>
          tag.title.split(' ').join('-').toLowerCase() === currentTag
        )
      );
    }

    if (brand) {
      filteredToRecipes = filteredToRecipes.filter(recipe => recipe.brand === brand);
    }

    if (searchText) {
      const searchTerms = searchText.toLowerCase().split(' ');
      filteredToRecipes = filteredToRecipes.filter(recipe =>
        searchTerms.every(term => recipe.title.toLowerCase().includes(term))
      );
    }

    return filteredToRecipes;
  }, [searchText, currentTag, brand, initialFilteredRecipes]);

  return (
    <section className={`recipe-archive ${theme || ""}`}>
      <div className="recipe-archive__inner">
        <div className="archive__filter" data-sal="slide-up" data-sal-duration="1000" data-sal-easing="ease">
          <form className="filter" onSubmit={(e) => handleSubmit(e)}>
            <div className="filter__select">
              <Select
                openMenuOnFocus={true}
                placeholder="Filter"
                options={options}
                className="react-select"
                classNamePrefix="react-select"
                isSearchable={false}
                onChange={(e) => setCurrentTag(e.value)}
              />
            </div>
            <div className="filter__input">
              <input onChange={(e) => setSearchText(e.target.value)} type="text" id="search" name="search"
                     placeholder="Search for something" />
              <button type="submit">
                <Search />
              </button>
            </div>
          </form>
        </div>
        {title && <h2 data-sal="slide-up" data-sal-duration="1000" data-sal-easing="ease">{parse(title)}</h2>}
        {
          !theme &&
          <div className="recipe-archive__brands">
            {items?.length > 0 && items.map((item, index) => (
              <div key={index} className={`tbi__item ${item.brand}`} data-sal="slide-up" data-sal-duration="1000"
                   data-sal-easing="ease">
                <div className="tbi__item-brand">
                  {
                    (() => {
                      switch (item.brand) {
                        case "woods":
                          return <WoodsLogo />
                        case "bone":
                          return <BoneLogo />
                        case "greenwheat":
                          return <GreenWheatLogo />
                        default:
                          return <EdlynLogo />
                      }
                    })()
                  }
                </div>
                <div className="tbi__item-content">
                  <p>{item.content}</p>
                  <button onClick={(e) => {
                    // scroll to #recipes
                    document.getElementById("recipes").scrollIntoView({ behavior: "smooth" });
                    setBrand(item.brand)
                  }} className="btn">{item.button}</button>
                </div>
                {item.src && <img className="atl-asset" src={item.src} alt={item.brand} />}
              </div>
            ))}
          </div>
        }
        <div className="recipe-archive__items" id="recipes">
          {
            filteredRecipes?.length > 0 && filteredRecipes.map((el, i) => (
              <Link to={el.permalink} key={i} className="recipe-archive__item" data-sal="slide-up" data-sal-duration="1000"
                    data-sal-easing="ease">
                <RecipeCard brand={el.brand} theme={theme} {...el} />
              </Link>
            ))
          }
        </div>
      </div>
    </section>
  )
}

export default RecipeArchive

export const query = graphql`
fragment RecipeArchiveProps on DatoCmsRecipeArchive {
  ... on DatoCmsRecipeArchive {
    id
    title
    brand
  }
}
`
